import { useQuery } from '@tanstack/react-query';
import { Fragment, useMemo, useState } from 'react';
import { useAuth } from '@gs/auth';
import { ApiUnauthorizedError } from '@gs/api-client';
import { api } from '../services/api';

interface LogItem {
  id: string;
  appId: string;
  appKey: string;
  appName: string;
  direction: string;
  startedAt: string;
  endedAt: string | null;
  status: string;
  records: number | null;
  durationMs: number | null;
  message: string | null;
  sourceKey: string | null;
  /** Sous-tâches rattachées (mig 165). 0 pour les runs antérieurs à la migration. */
  childCount: number;
  /** Sous-tâches en erreur — affiché SANS dépliage sur la ligne racine. */
  childErrorCount: number;
}

/** Sous-tâche d'un run, chargée au dépliage via /api/logs/:id/children. */
interface LogChild {
  id: string;
  stepName: string | null;
  sourceKey: string | null;
  startedAt: string;
  endedAt: string | null;
  status: string;
  records: number | null;
  durationMs: number | null;
  message: string | null;
}

interface LogsResponse {
  items: LogItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface AppLite {
  id: string;
  appKey: string;
  appName: string;
}

interface WatchdogAlert {
  tableName: string;
  fullName: string;
  status: string | null;
  message: string | null;
  rowsInTable: number | null;
  rowsLoaded: number | null;
  deltaVsPrev: number | null;
  deltaPct: number | null;
  lastWatermark: string | null;
  lastRunAt: string | null;
}

interface WatchdogStatus {
  total: number;
  okCount: number;
  warnCount: number;
  errorCount: number;
  hasIssues: boolean;
  alerts: WatchdogAlert[];
  allTables: WatchdogAlert[];
}

function WatchdogPanel() {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);

  // Poll watchdog 5 min + guard 401 (BUG-AUTH-401-LOOP-SYNC-STATUS-RUNNING) :
  // - 5 min normal (faible risque vs 3s SyncManager)
  // - false dès qu'une erreur d'auth survient (cohérence avec G_POLL_03)
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'watchdog-status'],
    queryFn: () => api.get<WatchdogStatus>('/api/etl/watchdog-status'),
    enabled: !!user,
    refetchInterval: (q) =>
      q.state.error instanceof ApiUnauthorizedError ? false : 5 * 60 * 1000,
    retry: false,
  });

  if (isLoading || !data) return null;

  if (!data.hasIssues) {
    return (
      <div className="overflow-hidden rounded-xl border border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/20">
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="flex w-full touch-manipulation items-center justify-between gap-3 px-4 py-2 text-left"
        >
          <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
            <svg viewBox="0 0 16 16" className="h-4 w-4 shrink-0 fill-current">
              <circle cx="8" cy="8" r="7" className="opacity-20" />
              <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>
              Watchdog données — <strong>{data.okCount}/{data.total}</strong> tables OK
            </span>
          </div>
          <svg
            viewBox="0 0 10 10"
            className={`h-3 w-3 shrink-0 fill-none stroke-current stroke-[1.5] text-green-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
          >
            <polyline points="2,3.5 5,6.5 8,3.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {expanded && (
          <div className="border-t border-green-200 dark:border-green-900 px-4 py-3">
            <div className="overflow-x-auto">
              <table className="w-full text-xs tabular-nums">
                <thead>
                  <tr className="text-left text-meta text-green-700 dark:text-green-500">
                    <th className="pb-1 pr-4 font-semibold">Table</th>
                    <th className="pb-1 pr-4 font-semibold text-right">Rows</th>
                    <th className="pb-1 pr-4 font-semibold text-right">Δ</th>
                    <th className="pb-1 pr-4 font-semibold">Watermark</th>
                    <th className="pb-1 font-semibold">Dernier run</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-green-100 dark:divide-green-900/40">
                  {(data.allTables ?? []).map((t) => (
                    <tr key={t.fullName} className="text-slate-600 dark:text-slate-400">
                      <td className="py-1 pr-4 font-mono text-meta font-medium text-slate-700 dark:text-slate-300">
                        {t.tableName}
                      </td>
                      <td className="py-1 pr-4 text-right">
                        {t.rowsInTable != null ? t.rowsInTable.toLocaleString('fr-CA') : '—'}
                      </td>
                      <td
                        className={`py-1 pr-4 text-right ${
                          t.deltaVsPrev != null && t.deltaVsPrev < 0
                            ? 'text-amber-600 dark:text-amber-400'
                            : ''
                        }`}
                      >
                        {t.deltaVsPrev != null
                          ? `${t.deltaVsPrev > 0 ? '+' : ''}${t.deltaVsPrev.toLocaleString('fr-CA')}`
                          : '—'}
                      </td>
                      <td className="py-1 pr-4">{t.lastWatermark ?? '—'}</td>
                      <td className="py-1">
                        {t.lastRunAt
                          ? new Date(t.lastRunAt).toLocaleString('fr-CA', { dateStyle: 'short', timeStyle: 'short' })
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  const isError = data.errorCount > 0;
  return (
    <div
      className={`overflow-hidden rounded-xl border ${
        isError
          ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/20'
          : 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/20'
      }`}
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full touch-manipulation items-center justify-between gap-3 px-4 py-2 text-left"
      >
        <div className="flex items-center gap-2">
          <span className={`text-lg ${isError ? 'text-red-500' : 'text-amber-500'}`}>
            {isError ? '❌' : '⚠️'}
          </span>
          <span
            className={`text-sm font-medium ${
              isError ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-600'
            }`}
          >
            Watchdog données —{' '}
            {data.errorCount > 0 && (
              <>
                {data.errorCount} erreur{data.errorCount > 1 ? 's' : ''}{' '}
              </>
            )}
            {data.warnCount > 0 && (
              <>
                {data.warnCount} avertissement{data.warnCount > 1 ? 's' : ''}
              </>
            )}{' '}
            sur {data.total} tables
          </span>
        </div>
        <svg
          viewBox="0 0 10 10"
          className={`h-3 w-3 shrink-0 fill-none stroke-current stroke-[1.5] transition-transform ${
            isError ? 'text-red-500' : 'text-amber-500'
          } ${expanded ? 'rotate-180' : ''}`}
        >
          <polyline points="2,3.5 5,6.5 8,3.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {expanded && (
        <div className="space-y-2 border-t border-current/10 px-4 py-3">
          {data.alerts.map((alert) => (
            <div
              key={alert.fullName}
              className={`rounded-lg p-3 text-xs ${
                alert.status === 'error'
                  ? 'bg-red-100 dark:bg-red-950/40'
                  : 'bg-amber-100 dark:bg-amber-950/40'
              }`}
            >
              <div className="mb-1 flex items-start justify-between gap-2">
                <span
                  className={`font-mono text-field font-semibold ${
                    alert.status === 'error' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-600'
                  }`}
                >
                  {alert.status === 'error' ? '✗' : '⚠'} {alert.tableName}
                </span>
                <span
                  className={`rounded-sm px-1.5 py-0.5 text-meta font-medium ${
                    alert.status === 'error'
                      ? 'bg-red-200 text-red-700 dark:bg-red-900 dark:text-red-300'
                      : 'bg-amber-200 text-amber-700 dark:bg-amber-900 dark:text-amber-300'
                  }`}
                >
                  {alert.status}
                </span>
              </div>

              {alert.message && (
                <p className="mb-2 text-slate-600 dark:text-slate-400">{alert.message}</p>
              )}

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-meta tabular-nums text-slate-500 dark:text-slate-400">
                {alert.rowsInTable != null && (
                  <span>
                    rows_in_table: <strong>{alert.rowsInTable.toLocaleString('fr-CA')}</strong>
                  </span>
                )}
                {alert.deltaVsPrev != null && (
                  <span>
                    delta:{' '}
                    <strong className={alert.deltaVsPrev < 0 ? 'text-red-600' : ''}>
                      {alert.deltaVsPrev > 0 ? '+' : ''}
                      {alert.deltaVsPrev.toLocaleString('fr-CA')}
                      {alert.deltaPct != null && ` (${alert.deltaPct}%)`}
                    </strong>
                  </span>
                )}
                {alert.lastWatermark && (
                  <span>
                    watermark: <strong>{alert.lastWatermark}</strong>
                  </span>
                )}
                {alert.lastRunAt && (
                  <span>
                    dernier run:{' '}
                    <strong>
                      {new Date(alert.lastRunAt).toLocaleString('fr-CA', {
                        dateStyle: 'short',
                        timeStyle: 'short',
                      })}
                    </strong>
                  </span>
                )}
              </div>
            </div>
          ))}

          <p className="pt-1 text-meta text-slate-400 dark:text-slate-500">
            Ces alertes proviennent de <code>adm.EtlControl</code> — watchdog qualité par table. Le
            statut du run global (ci-dessous) peut être OK même si des avertissements sont actifs.
          </p>
        </div>
      )}
    </div>
  );
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  if (ms >= 60000) return `${Math.round(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  if (ms >= 1000) return `${Math.round(ms / 1000)}s`;
  return `${ms}ms`;
}

/**
 * Sous-tâches d'un run — chargées à la demande (volet B3/C3).
 *
 * Monté uniquement quand la ligne est dépliée : la liste des runs ne paie jamais
 * le coût des étapes. Un run SOFI en porte une quarantaine.
 */
function SyncLogChildren({ logId }: { logId: string }) {
  const { user } = useAuth();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'log-children', logId],
    queryFn: () => api.get<{ items: LogChild[] }>(`/api/logs/${logId}/children`),
    enabled: !!user,
    // Un run terminé ne change plus : inutile de refetcher à chaque dépliage.
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return <div className="h-16 animate-pulse rounded-lg bg-slate-200 dark:bg-slate-700" />;
  }
  if (isError) {
    return (
      <div className="text-xs text-red-600 dark:text-red-400">
        Impossible de charger les sous-tâches.{' '}
        <button type="button" className="underline touch-manipulation" onClick={() => refetch()}>
          Réessayer
        </button>
      </div>
    );
  }

  const items = data?.items ?? [];
  if (items.length === 0) {
    return <div className="text-xs text-slate-500 dark:text-slate-500">Aucune sous-tâche.</div>;
  }

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-meta text-slate-500 dark:text-slate-500">
          <th className="pb-1 pr-4 font-semibold">Étape</th>
          <th className="pb-1 pr-4 font-semibold">Statut</th>
          <th className="pb-1 pr-4 text-right font-semibold">Lignes</th>
          <th className="pb-1 pr-4 text-right font-semibold">Durée</th>
          <th className="pb-1 font-semibold">Détail</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
        {items.map((c) => {
          const isStepError = c.status === 'error';
          // `skipped` (ADR-075) : l'étape a REFUSÉ de calculer, sur une source en échec
          // dans le run ou périmée hors run. Sans cette branche elle héritait du vert du
          // cas par défaut — un statut neuf absorbé en silence par un lecteur existant,
          // soit exactement le défaut que ce statut corrige.
          const isStepSkipped = c.status === 'skipped';
          return (
            <tr
              key={c.id}
              className={
                isStepError
                  ? 'text-red-700 dark:text-red-400'
                  : isStepSkipped
                    ? 'text-amber-700 dark:text-amber-500'
                    : 'text-slate-600 dark:text-slate-400'
              }
            >
              <td className="py-1 pr-4 font-mono font-medium text-slate-700 dark:text-slate-300">
                {c.stepName ?? '—'}
              </td>
              <td className="py-1 pr-4">
                <span
                  className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-meta font-medium ${
                    isStepError
                      ? 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400'
                      : isStepSkipped
                        ? 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-500'
                        : 'bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400'
                  }`}
                >
                  {c.status}
                </span>
              </td>
              <td className="py-1 pr-4 text-right tabular-nums">
                {c.records != null ? c.records.toLocaleString('fr-CA') : '—'}
              </td>
              <td className="py-1 pr-4 text-right tabular-nums">{formatDuration(c.durationMs)}</td>
              <td className="py-1 break-words text-meta">{c.message ?? '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function LogsPage() {
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [appId, setAppId] = useState('');
  const [direction, setDirection] = useState<'all' | 'in' | 'out'>('all');
  const [status, setStatus] = useState<'all' | 'success' | 'warning' | 'error' | 'running'>('all');

  const { data: apps } = useQuery({
    queryKey: ['admin', 'apps'],
    queryFn: () => api.get<AppLite[]>('/api/apps'),
    enabled: !!user,
  });

  const params = useMemo(() => {
    const p: Record<string, string | number> = { page, pageSize: 15 };
    if (appId) p.appId = appId;
    if (direction !== 'all') p.direction = direction;
    if (status !== 'all') p.status = status;
    return p;
  }, [page, appId, direction, status]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'logs', params],
    queryFn: () => api.get<LogsResponse>('/api/logs', params),
    enabled: !!user,
  });

  function parseSteps(message: string) {
    return message.split(' | ').map((part) => {
      const colonIdx = part.indexOf(':');
      if (colonIdx === -1) return { name: part, result: '', isError: false };
      const name = part.slice(0, colonIdx).trim();
      const rest = part.slice(colonIdx + 1).trim();
      const isError = rest.startsWith('error');
      return { name, result: rest, isError };
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Journaux ETL</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Une ligne par synchronisation — cliquer pour voir les sous-tâches
        </p>
      </div>

      <WatchdogPanel />

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-xs dark:border-slate-700 dark:bg-slate-900">
        <label className="flex flex-col gap-1 text-xs text-slate-600">
          Application
          <select
            value={appId}
            onChange={(e) => {
              setAppId(e.target.value);
              setPage(1);
            }}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2 min-w-44"
          >
            <option value="">Toutes</option>
            {(apps ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.appName}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-600">
          Direction
          <select
            value={direction}
            onChange={(e) => {
              setDirection(e.target.value as typeof direction);
              setPage(1);
            }}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2"
          >
            <option value="all">Toutes</option>
            <option value="in">Inbound</option>
            <option value="out">Outbound</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-600">
          Statut
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as typeof status);
              setPage(1);
            }}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2"
          >
            <option value="all">Tous</option>
            <option value="success">success</option>
            <option value="warning">warning</option>
            <option value="error">error</option>
            <option value="running">running</option>
          </select>
        </label>
      </div>

      {isLoading && <div className="animate-pulse h-48 bg-slate-200 rounded-xl" />}
      {isError && (
        <div className="text-red-600 text-sm">
          Erreur.{' '}
          <button type="button" className="underline" onClick={() => refetch()}>
            Réessayer
          </button>
        </div>
      )}

      {data && (
        <>
          <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Horodatage</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">App</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Direction</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Statut</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Records</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Durée</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                      Aucune entrée
                    </td>
                  </tr>
                ) : (
                  data.items.map((l) => {
                    const isExpanded = expandedId === l.id;
                    const hasMessage = Boolean(l.message);
                    const steps = l.message ? parseSteps(l.message) : [];
                    // Deux sources de sous-tâches, par ordre de fiabilité :
                    //  - `childCount` : lignes SyncLogs rattachées (structurées,
                    //    avec durée et statut propres) — runs postérieurs à mig 165 ;
                    //  - `message` : résumé texte concaténé du run racine — seule
                    //    source pour l'historique, conservée telle quelle.
                    const hasChildren = l.childCount > 0;
                    const isExpandable = hasChildren || hasMessage;
                    const errorCount = hasChildren
                      ? l.childErrorCount
                      : steps.filter((s) => s.isError).length;
                    const stepCount = hasChildren ? l.childCount : steps.length;

                    return (
                      <Fragment key={l.id}>
                        <tr
                          onClick={() => isExpandable && setExpandedId(isExpanded ? null : l.id)}
                          className={`border-b border-slate-200 dark:border-slate-700 transition-colors touch-manipulation ${
                            isExpandable ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50' : ''
                          } ${isExpanded ? 'bg-slate-50 dark:bg-slate-800/50' : ''}`}
                        >
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 dark:text-slate-400 tabular-nums">
                            {new Date(l.startedAt).toLocaleString('fr-CA', {
                              dateStyle: 'short',
                              timeStyle: 'medium',
                            })}
                          </td>
                          <td className="px-3 py-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                            {l.appName}
                          </td>
                          <td className="px-3 py-2 text-sm text-slate-600 dark:text-slate-400">{l.direction}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                l.status === 'ok' || l.status === 'success'
                                  ? 'bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400'
                                  : l.status === 'error'
                                    ? 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400'
                                    : l.status === 'running'
                                      ? 'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400'
                                      : 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400'
                              }`}
                            >
                              {l.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right text-sm tabular-nums text-slate-600 dark:text-slate-400">
                            {l.records != null ? l.records.toLocaleString('fr-CA') : '—'}
                          </td>
                          <td className="px-3 py-2 text-right text-sm tabular-nums text-slate-600 dark:text-slate-400">
                            {formatDuration(l.durationMs)}
                          </td>
                          <td className="px-3 py-2">
                            {isExpandable ? (
                              <div className="flex items-center gap-2">
                                {errorCount > 0 && (
                                  <span className="text-xs px-1.5 py-0.5 rounded-sm bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 font-medium shrink-0">
                                    {errorCount} erreur{errorCount > 1 ? 's' : ''}
                                  </span>
                                )}
                                <span className="text-xs text-slate-500 dark:text-slate-500 truncate max-w-xs">
                                  {stepCount} étape{stepCount > 1 ? 's' : ''}
                                </span>
                                <svg
                                  viewBox="0 0 10 10"
                                  className={`w-3 h-3 shrink-0 fill-none stroke-current stroke-[1.5] text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                >
                                  <polyline points="2,3.5 5,6.5 8,3.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </div>
                            ) : (
                              <span className="text-sm text-slate-500">—</span>
                            )}
                          </td>
                        </tr>

                        {/* Sous-tâches structurées (mig 165) — chargées au dépliage. */}
                        {isExpanded && hasChildren && (
                          <tr key={`${l.id}-children`} className="bg-slate-50 dark:bg-slate-800/50">
                            <td colSpan={7} className="px-4 py-3">
                              <SyncLogChildren logId={l.id} />
                            </td>
                          </tr>
                        )}

                        {/* Repli historique : runs antérieurs à la migration, dont les
                            étapes n'existent que concaténées dans `message`. */}
                        {isExpanded && !hasChildren && steps.length > 0 && (
                          <tr key={`${l.id}-detail`} className="bg-slate-50 dark:bg-slate-800/50">
                            <td colSpan={7} className="px-4 py-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                                {steps.map((step, i) => (
                                  <div
                                    key={i}
                                    className={`flex items-start gap-2 px-2 py-1 rounded-sm text-xs ${
                                      step.isError
                                        ? 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400'
                                        : 'text-slate-600 dark:text-slate-400'
                                    }`}
                                  >
                                    <span
                                      className={`shrink-0 mt-px ${step.isError ? 'text-red-500' : 'text-green-500'}`}
                                    >
                                      {step.isError ? '✗' : '✓'}
                                    </span>
                                    <div className="min-w-0">
                                      <div className="font-mono font-medium truncate">{step.name}</div>
                                      {step.isError ? (
                                        <div className="text-meta text-red-600 dark:text-red-400 break-words">
                                          {step.result.replace(/^error \(/, '').replace(/\)$/, '')}
                                        </div>
                                      ) : (
                                        <div className="text-meta text-slate-500 dark:text-slate-500 tabular-nums">
                                          {step.result}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>
              Page {data.page} / {Math.max(1, data.totalPages)} — {data.total} ligne(s)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 rounded-lg border border-slate-200 disabled:opacity-40"
              >
                Précédent
              </button>
              <button
                type="button"
                disabled={page >= data.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 rounded-lg border border-slate-200 disabled:opacity-40"
              >
                Suivant
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
